import React, { useContext, useEffect, useState } from 'react';

import { TaskEntity } from '../../tasks/Task.interface';
import {
  useCreateProductDesignSpec,
  useProductEntityPrefixId,
  useProductViewParams,
  useUpdateProductDesignSpec
} from '../Product.hooks';
import {
  BaseProductBoardColumnProps,
  ProductDesignSpec
} from '../Product.interface';
import { ProductBoardContext } from './ProductBoard.context';
import { ProductBoardColumnEntity } from './ProductBoardColumn';
import ProductBoardItem from './ProductBoardItem';

export type ProductBoardDesignSpecProps = BaseProductBoardColumnProps<
  ProductDesignSpec,
  'designSpec'
>;

const ProductBoardDesignSpec: React.FC<ProductBoardDesignSpecProps> = (
  props: ProductBoardDesignSpecProps
) => {
  const {
    product,
    collapsed,
    designSpec: initialDesignSpec,
    onCancel,
    onError,
    onSave
  } = props;
  const entity = ProductBoardColumnEntity.DesignSpec;
  const params: string = useProductViewParams();
  const { toggleSelected, isSelected } = useContext(ProductBoardContext);
  const [designSpec, setDesignSpec] = useState(initialDesignSpec);
  useEffect(() => {
    if (initialDesignSpec) {
      setDesignSpec(initialDesignSpec);
    }
  }, [initialDesignSpec]);
  const {
    createdProductDesignSpec,
    isCreatingProductDesignSpec,
    createProductDesignSpecError,
    createProductDesignSpec
  } = useCreateProductDesignSpec(product.id);
  const {
    updatedProductDesignSpec,
    isUpdatingProductDesignSpec,
    updateProductDesignSpec
  } = useUpdateProductDesignSpec(product.id, designSpec.id || 0);
  const entityId: string = useProductEntityPrefixId(
    designSpec,
    ProductBoardColumnEntity.DesignSpec
  );

  useEffect(() => {
    if (createdProductDesignSpec) {
      setDesignSpec(createdProductDesignSpec);
      onSave?.(createdProductDesignSpec, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdProductDesignSpec]);

  useEffect(() => {
    if (updatedProductDesignSpec) {
      setDesignSpec(updatedProductDesignSpec);
      onSave?.(updatedProductDesignSpec);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedProductDesignSpec]);

  useEffect(() => {
    if (createProductDesignSpecError) {
      onError?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createProductDesignSpecError]);

  return (
    <ProductBoardItem
      product={product}
      taskEntity={designSpec as TaskEntity}
      title={designSpec.title || ''}
      entityId={entityId}
      collapsed={collapsed}
      editable={!designSpec.id}
      isSelected={isSelected(designSpec.id, entity)}
      loading={isUpdatingProductDesignSpec || isCreatingProductDesignSpec}
      viewDetailsLink={`/portal/products/${designSpec.productId}/design-specs/${designSpec.id}${params}`}
      onSelect={() => toggleSelected(designSpec, entity)}
      onCancel={() => {
        setDesignSpec(initialDesignSpec);
        onCancel?.();
      }}
      onSave={title => {
        designSpec.title = title;
        if (designSpec.id) {
          updateProductDesignSpec(designSpec);
        } else {
          createProductDesignSpec(designSpec);
        }
      }}
    />
  );
};

export default ProductBoardDesignSpec;
